import { motion } from 'framer-motion';
import { Link, Navigate } from '@/components/LocalizedLink';
import { useParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/SEOHead';
import { canonicalBlogTagSlug, getBlogLogicalPath } from '@/lib/blogSlugs';
import { buildBlogTagPageModel } from '@/lib/blogTagPageModel';
import { SITE_URL, buildAbsoluteUrl } from '@/lib/seo';
import { localizePath } from '@/lib/localeRouting';
import { buildBreadcrumbSchema, buildCollectionItemListSchema } from '@/lib/schema';
import NotFound from '@/pages/NotFound';

const BlogTagPage = () => {
  const { tagSlug } = useParams<{ tagSlug: string }>();
  const { lang, t } = useLanguage();
  const canonicalSlug = tagSlug ? canonicalBlogTagSlug(tagSlug) : '';

  if (!canonicalSlug) return <NotFound />;
  if (canonicalSlug !== tagSlug) return <Navigate to={`/blog/tag/${canonicalSlug}`} replace />;

  const model = buildBlogTagPageModel(canonicalSlug, lang);
  if (!model) return <NotFound />;

  const logicalPath = `/blog/tag/${canonicalSlug}`;
  const dateFormatter = new Intl.DateTimeFormat(lang === 'tr' ? 'tr-TR' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
  const formatDate = (iso: string) => dateFormatter.format(new Date(`${iso}T00:00:00Z`));
  const minutesLabel = (minutes: number) => (lang === 'tr' ? `${minutes} dk okuma` : `${minutes} min read`);
  const singleLocale = model.entry.locales.length === 1;

  const itemListSchema = buildCollectionItemListSchema({
    name: model.title,
    description: model.description,
    canonicalPath: logicalPath,
    items: model.posts.map((post, index) => ({
      name: post.title[lang],
      url: buildAbsoluteUrl(SITE_URL, localizePath(getBlogLogicalPath(post, lang), lang)),
      position: index + 1,
    })),
  });

  const breadcrumbSchema = buildBreadcrumbSchema([
    { name: lang === 'tr' ? 'Ana Sayfa' : 'Home', item: buildAbsoluteUrl(SITE_URL, localizePath('/', lang)) },
    { name: 'Blog', item: buildAbsoluteUrl(SITE_URL, localizePath('/blog', lang)) },
    { name: model.label, item: buildAbsoluteUrl(SITE_URL, localizePath(logicalPath, lang)) },
  ]);

  return (
    <main className="pt-32 pb-20">
      <SEOHead
        title={model.title}
        description={model.description}
        canonical={logicalPath}
        noindex={model.entry.indexable ? false : 'follow'}
        localeAlternatePaths={singleLocale ? { [lang]: logicalPath } : undefined}
        jsonLd={[itemListSchema, breadcrumbSchema]}
      />

      <div className="container mx-auto px-6 lg:px-12">
        <Link to="/blog" className="font-body text-sm text-muted-foreground hover:text-foreground mb-8 inline-block">
          ← {t('blog.back')}
        </Link>
        <motion.header
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl mb-12"
        >
          <p className="font-body text-xs tracking-[0.3em] text-muted-foreground uppercase mb-3">
            {lang === 'tr' ? 'Etiket' : 'Tag'}
          </p>
          <h1 className="font-display text-4xl lg:text-6xl font-bold text-foreground mb-6">{model.title}</h1>
          {model.intro.map((paragraph) => (
            <p key={paragraph} className="font-body text-lg text-muted-foreground leading-relaxed mb-3">
              {paragraph}
            </p>
          ))}
          <dl className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 font-body text-sm">
            <div>
              <dt className="text-muted-foreground">{lang === 'tr' ? 'Yazı' : 'Articles'}</dt>
              <dd className="text-foreground font-semibold">{model.count}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{lang === 'tr' ? 'Toplam okuma' : 'Total reading'}</dt>
              <dd className="text-foreground font-semibold">{lang === 'tr' ? `${model.totalMinutes} dk` : `${model.totalMinutes} min`}</dd>
            </div>
            {model.first ? (
              <div>
                <dt className="text-muted-foreground">{lang === 'tr' ? 'İlk yazı' : 'First article'}</dt>
                <dd className="text-foreground font-semibold">{formatDate(model.first)}</dd>
              </div>
            ) : null}
            {model.last ? (
              <div>
                <dt className="text-muted-foreground">{lang === 'tr' ? 'Son yazı' : 'Latest article'}</dt>
                <dd className="text-foreground font-semibold">{formatDate(model.last)}</dd>
              </div>
            ) : null}
          </dl>
        </motion.header>

        <section aria-labelledby="tag-posts" className="mb-16">
          <h2 id="tag-posts" className="font-display text-2xl font-bold text-foreground mb-6">
            {lang === 'tr' ? `“${model.label}” etiketli yazılar` : `Articles tagged “${model.label}”`}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {model.posts.map((post) => (
              <Link
                key={post.slug}
                to={getBlogLogicalPath(post, lang)}
                className="block rounded-2xl border border-border p-6 hover:bg-secondary/20 transition-colors"
              >
                <p className="font-body text-xs text-muted-foreground mb-2">
                  {[post.category[lang], post.publishedAt ? formatDate(post.publishedAt) : '', post.readingMinutes[lang] ? minutesLabel(post.readingMinutes[lang]) : '']
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                <h3 className="font-display text-xl font-bold text-foreground mb-2">{post.title[lang]}</h3>
                <p className="font-body text-sm text-muted-foreground line-clamp-3">{post.excerpt[lang]}</p>
              </Link>
            ))}
          </div>
        </section>

        {model.topics.length ? (
          <section aria-labelledby="tag-topics" className="max-w-3xl mb-16">
            <h2 id="tag-topics" className="font-display text-2xl font-bold text-foreground mb-4">
              {lang === 'tr' ? 'Bu yazılarda ele alınan başlıklar' : 'Topics covered in these articles'}
            </h2>
            <ul className="space-y-2 font-body text-sm text-muted-foreground list-disc pl-5">
              {model.topics.map(({ heading, post }) => (
                <li key={`${post.slug}-${heading}`}>
                  {heading}{' '}
                  <span className="whitespace-nowrap">
                    —{' '}
                    <Link to={getBlogLogicalPath(post, lang)} className="underline underline-offset-4 hover:text-foreground">
                      {post.title[lang]}
                    </Link>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section aria-labelledby="tag-summary" className="max-w-3xl mb-16">
          <h2 id="tag-summary" className="font-display text-2xl font-bold text-foreground mb-4">
            {lang === 'tr' ? 'Kısa özet' : 'Quick summary'}
          </h2>
          <dl className="space-y-5 font-body">
            {model.faq.map((item) => (
              <div key={item.question}>
                <dt className="text-foreground font-semibold mb-1">{item.question}</dt>
                <dd className="text-muted-foreground leading-relaxed">{item.answer}</dd>
              </div>
            ))}
          </dl>
        </section>

        {model.relatedTags.length ? (
          <section aria-labelledby="tag-related" className="mb-16">
            <h2 id="tag-related" className="font-display text-2xl font-bold text-foreground mb-4">
              {lang === 'tr' ? 'İlgili etiketler' : 'Related tags'}
            </h2>
            <div className="flex flex-wrap gap-2">
              {model.relatedTags.map((tag) => (
                <Link
                  key={tag.slug}
                  to={`/blog/tag/${tag.slug}`}
                  className="font-body text-xs tracking-[0.08em] uppercase bg-secondary px-3 py-1.5 rounded-full text-muted-foreground hover:text-foreground transition-colors"
                >
                  {tag.label} · {tag.shared}
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        <section aria-labelledby="tag-services" className="mb-8">
          <h2 id="tag-services" className="font-display text-2xl font-bold text-foreground mb-4">
            {lang === 'tr' ? 'İlgili hizmetler ve araçlar' : 'Related services and tools'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {model.services.map((service) => (
              <Link
                key={service.slug}
                to={`/services/${service.slug}`}
                className="block rounded-2xl border border-border p-6 hover:bg-secondary/20 transition-colors"
              >
                <h3 className="font-display text-lg font-bold text-foreground mb-2">{service.title}</h3>
                <p className="font-body text-sm text-muted-foreground">{service.intro}</p>
              </Link>
            ))}
          </div>
          <ul className="flex flex-wrap gap-4 font-body text-sm">
            {model.tools.map((tool) => (
              <li key={tool.id}>
                <Link to={`/tools/${tool.id}`} className="underline underline-offset-4 text-foreground hover:text-muted-foreground">
                  {tool.title}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </main>
  );
};

export default BlogTagPage;
